/**
 * Converte valor monetário em reais para centavos
 * @param value - Valor em reais (ex: 100.50)
 * @returns Valor em centavos (ex: 10050)
 */
export const currencyToCents = (value: number): number => {
  return Math.round(value * 100);
};

/**
 * Converte valor em centavos para reais
 * @param cents - Valor em centavos (ex: 10050)
 * @returns Valor em reais (ex: 100.50)
 */
export const centsToDecimal = (cents: number): number => {
  return cents / 100;
};

/**
 * Converte uma porcentagem decimal para basis points (inteiros para o backend)
 * Reutiliza a lógica de currencyToCents pois matematicamente é idêntica
 * @param percentage - Porcentagem como decimal (ex: 84.45)
 * @returns Valor em basis points (ex: 8445)
 */
export const percentageToBasisPoints = (percentage: number): number => {
  return currencyToCents(percentage);
};

/**
 * Converte basis points (inteiros do backend) para porcentagem decimal
 * Reutiliza a lógica de centsToDecimal pois matematicamente é idêntica
 * @param basisPoints - Valor em basis points (ex: 8445)
 * @returns Porcentagem como decimal (ex: 84.45)
 */
export const basisPointsToPercentage = (basisPoints: number): number => {
  return centsToDecimal(basisPoints);
};

/**
 * Formata um valor monetário em reais para uma string com o formato R$ 1.000.000,00
 * @param value - Valor em reais (ex: 1000000.00)
 * @returns Valor formatado (ex: R$ 1.000.000,00)
 */
export const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)} mi`;
  } else if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)} mil`;
  }
  return `R$ ${value.toFixed(0).replace('.', ',')}`;
};

/**
 * Formata valor em BRL (reais) usando notação compacta localizada
 * (ex.: R$ 1,2 mil, R$ 2,5 mi, R$ 22,5 bi, R$ 1,5 tri).
 */
const brlCompactFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

export const formatBrlCompact = (valueInReais: number): string => {
  const normalizedValue = Number.isFinite(valueInReais) ? valueInReais : 0;

  return brlCompactFormatter.format(normalizedValue);
};
